import { Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogContentText, DialogProps, DialogTitle, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, Switch, TextField } from '@mui/material'
import Grid from "@mui/material/Grid";
import APIRequest, { APIError } from 'system/APIRequest'
import React, { useState } from 'react'
import i18n from 'translation'
import { PersonAdd } from '@mui/icons-material'

export interface InviteDialogProps {
  accountId: string | undefined
  handleComplete?: (wasSuccessful: boolean, numberInvited: number, error?: APIError) => void
}

export function InviteDialog (props: DialogProps & InviteDialogProps) {
  const [isRunning, setIsRunning] = useState(false)
  const [totalCompleted, setTotalCompleted] = useState(0)
  const [apiError, setApiError] = useState<string | undefined>()
  const [userRole, setUserRole] = useState('view-only');
  const [adminRole, setAdminRole] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const handleClose = (e: {}, reason: 'escapeKeyDown' | 'backdropClick') => {
    if (!isRunning) {
      // On close, clear state, incase we're re-open
      setApiError(undefined)
      setTotalCompleted(0)
      setIsRunning(false)

      if (apiError) {
        // We report complete on close for errors
        props.handleComplete?.(false, totalCompleted, apiError)
      }

      if (props.onClose) {
        props.onClose(e, reason)
      }
    }
  }

  const performRequests = async () => {
    setIsRunning(true);
    setTotalCompleted(0);
    setApiError(undefined);

    // Build request parameters
    let requestURI = "admin/invite";
    if(props.accountId !== undefined) {
      requestURI += '/' + props.accountId
    }

    // await will block until complete
    await APIRequest(requestURI, { role: userRole, admin: adminRole ? "1" : "0" }, { method: "POST", body: emailInput }).then((res: any) => {
      if (res.invitations_failed) {
        // Something went wrong
        let err = `${res.invitations_failed}/${res.invitations_sent + res.invitations_failed} invitations failed`;
        res.results.forEach((r) => {
          if (!r.success) {
            err += `\n${r.error}`
          }
        });
        setApiError(err);
      } else {
        props.handleComplete?.(true, res.invitations_sent);
      }
    }).catch((reason: APIError) => {
      setApiError(reason.server_message || String(reason));
    }).finally(() => {
      setIsRunning(false);
    });
  }

  const handleSubmitClick = (e: any) => {
    if(!isRunning) {
      performRequests();
    }
  }

  // Extract the accountId we need from the props, rest are for the Dialog component
  const { accountId, handleComplete, ...dialogProps } = props

  return (
    <Dialog
      {...dialogProps}
      onClose={handleClose}
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()}
    >
      <DialogTitle>
        <PersonAdd color='success' style={{ marginBottom: '-5px', marginRight: '5px' }} />
        &nbsp;{i18n.t('invite users')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText style={{ marginBottom: '1rem', whiteSpace: 'pre-wrap' }}>
          {i18n.t("resource-action.dialog.invite-users-body")}
        </DialogContentText>

        <Grid container spacing={0} sx={{maxWidth: 400}} key="inviteUsersGrid">
          <Grid size={{ xs: 5 }}>
            <InputLabel id="selectUserRoleLabel">Role</InputLabel>
            <Select
              sx={{ width: '15ch'}}
              labelId="selectUserRoleLabel"
              id="selectUserRole"
              value={userRole}
              label="Role"
              onChange={(event: SelectChangeEvent, child?: object) => {
                setUserRole(event.target.value);
              }}
            >
              <MenuItem value={'view-only'}>Viewer</MenuItem>
              <MenuItem value={'quick-search'}>Quick Search</MenuItem>
              <MenuItem value={'analyst'}>Analyst</MenuItem>
              <MenuItem value={'api-only'}>API only</MenuItem>
              <MenuItem value={'ai-studio'}>Pulse AI only</MenuItem>
            </Select>
          </Grid>

          <Grid style={{ display: "flex" }} sx={{ my: 3 }}>
            <FormControlLabel
              control={
                <Switch checked={adminRole} onChange={(event: React.ChangeEvent) => {
                  setAdminRole(event.target.checked);
                }} name="Admin" />
              }
              label="Admin"
            />
          </Grid>
        </Grid>
        
        <DialogContentText sx={{ my: 0.5 }}>
          {i18n.t("resource-action.dialog.invite-users-input-help-text")}
        </DialogContentText>

        <TextField multiline={true} rows={5}
          value={emailInput}
          style={{ width: '100%' }}
          key={"inviteEmailInput"}
          autoComplete="off"
          size="small"
          variant={"outlined"}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setEmailInput(e.target.value);
            setSubmitDisabled(!(e.target.value.trim().length > 1 && e.target.value.includes('@')));
          }}
        />

        {apiError &&
          <Box sx={{
            mt: 1
          }}>
            <Alert severity="error" style={{whiteSpace: "pre-wrap"}}>{apiError.server_message || String(apiError)}</Alert>
          </Box>
        }

        <div style={{ marginTop: "1rem", display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={(e: any) => handleClose({}, "escapeKeyDown")}
            disabled={isRunning}
            color="inherit">
            {i18n.t("cancel")}
          </Button>

          

          <Box sx={{ m: 1, position: 'relative' }}>
            <Button
              onClick={handleSubmitClick}
              disabled={submitDisabled || isRunning}
              color={'success'}>
              {i18n.t('send invites')}
            </Button>

            {isRunning && 
              // Draw a circular loader over the submit button when running
              // Copy-paste from the MUI docs
              <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }}
            />}
          </Box>
        </div>

      </DialogContent>
    </Dialog>
  );
}
